import { useAuth0 } from "@auth0/auth0-react";
import { config } from "@config/config";
import { FolderIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/solid";
import { useMemo, useState } from "react";

import {
  Button,
  Folder,
  FolderExplorer,
  GoalBankCard,
  GoalBankPanel,
  UserType,
} from "@fronterahealth/frontera-ui-components";

import { useDeleteGoalBankGoalMutation, useDeleteGoalBankTargetMutation } from "@api/graphql/types-and-hooks";
import { ConfirmationDialog } from "@components/ConfirmationDialog/ConfirmationDialog";
import { GoalBankShortTermGoalsAddNewForm } from "@components/GoalsBank/SavedGoals/ShortTermGoals/GoalBankShortTermGoalsAddNewForm";
import {
  GoalBankShortTermGoalsEditForm,
  ShortTermGoalBankFormProps,
} from "@components/GoalsBank/SavedGoals/ShortTermGoals/GoalBankShortTermGoalsEditForm";
import { MoveShortTermGoalToFolder } from "@components/GoalsBank/SavedGoals/ShortTermGoals/GoalBankShortTermGoalsMoveToFolder";
import { GoalBankTargetsAddNewForm } from "@components/GoalsBank/SavedGoals/Targets/GoalBankTargetsGoalsAddNewForm";
import {
  GoalBankTargetsEditForm,
  TargetFormProps,
} from "@components/GoalsBank/SavedGoals/Targets/GoalBankTargetsGoalsEditForm";
import { MoveTargetGoalToFolder } from "@components/GoalsBank/SavedGoals/Targets/GoalBankTargetsMoveToFolder";
import { Goals } from "@components/GoalsBank/constants";
import { DeleteFolderDialog } from "@components/GoalsBank/dialogs/DeleteFolderDialog";
import { useGetShortTermGoals } from "@components/GoalsBank/hooks/useGetShortTermGoals";
import { useGetTargets } from "@components/GoalsBank/hooks/useGetTargets";
import { useGoalBankFolders } from "@components/GoalsBank/hooks/useGoalBankFolders";
import {
  findFolder,
  getFolderSelections,
  getOrgFolderSection,
  getTabsToDisplay,
  getUserFolderSection,
} from "@components/GoalsBank/utils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

export const SavedGoalsBank = () => {
  const { user } = useAuth0();
  const orgName = user![config.auth0Audience + "/orgName"] || "";
  const userType: UserType = !!user?.org_id ? "organization" : "selfServe";
  const [selectedTab, setSelectedTab] = useState<Goals>(Goals.SHORT_TERM_GOALS);
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");
  const [selectedGoal, setSelectedGoal] = useState<ShortTermGoalBankFormProps | TargetFormProps | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { userFolders, orgFolders, createNewFolder, deleteFolder, editFolder } = useGoalBankFolders(
    searchTerm,
    selectedTab === Goals.SHORT_TERM_GOALS,
  );
  const [openEditForm, setOpenEditForm] = useState<boolean>(false);

  const { mutateAsync: deleteGoalBankGoal } = useDeleteGoalBankGoalMutation({});
  const { mutateAsync: deleteTarget } = useDeleteGoalBankTargetMutation({});

  const { shortTermGoals, refetchStg, stgIsLoading } = useGetShortTermGoals(selectedFolderId, searchTerm);
  const { targets, refetchTargets, targetsIsLoading } = useGetTargets(selectedFolderId, searchTerm);

  const [isSTGMoveToFolderDialogOpen, setSTGMoveToFolderDialogOpen] = useState<boolean>(false);
  const [isTargetMoveToFolderDialogOpen, setTargetMoveToFolderDialogOpen] = useState<boolean>(false);

  const [openDeleteGoalDialog, setDeleteGoalDialogOpen] = useState(false);
  const [deleteGoalId, setDeleteGoalId] = useState<string>("");

  const deleteSTGoal = async () => {
    try {
      await deleteGoalBankGoal({
        input: {
          folderId: selectedFolderId,
          goalBankShortTermId: deleteGoalId,
        },
      });
      notifySuccess("Successfully Deleted Short Term Goal");
      await refetchStg();
    } catch (err) {
      console.error("Failed To Delete Short Term Goal", err);
      notifyError("Failed To Delete Short Term Goal");
    } finally {
      setDeleteGoalId("");
    }
  };

  const displayShortTermGoals = useMemo(() => {
    if (selectedTab !== Goals.SHORT_TERM_GOALS) return [];

    return (
      shortTermGoals?.getGoalBankGoals?.edges.map((edge) => {
        const goal = edge?.node;
        if (!goal) return null;

        return (
          <GoalBankCard
            key={goal.id}
            id={goal.id ?? ""}
            title={goal.goalName ?? ""}
            subtitle={goal.domain?.name ?? ""}
            body={goal.masteryCriteria ?? ""}
            onClick={() => {
              setSelectedGoal(goal as ShortTermGoalBankFormProps);
              setOpenEditForm(true);
            }}
            settings={{
              menuItems: [
                {
                  label: "Edit",
                  Icon: PencilIcon,
                  callback: () => {
                    setSelectedGoal(goal as ShortTermGoalBankFormProps);
                    setOpenEditForm(true);
                  },
                },
                {
                  label: "Move to Folder",
                  Icon: FolderIcon,
                  callback: () => {
                    setSelectedGoal(goal as ShortTermGoalBankFormProps);
                    setSTGMoveToFolderDialogOpen(true);
                  },
                },
                {
                  label: "Delete",
                  Icon: TrashIcon,
                  textColorClassName: "text-red-700",
                  callback: () => {
                    setDeleteGoalId(goal.id);
                    setDeleteGoalDialogOpen(true);
                  },
                },
              ],
              openDirection: "to-bottom-left",
            }}
          />
        );
      }) || []
    );
  }, [selectedTab, shortTermGoals, setSelectedGoal, setOpenEditForm]);

  const displayTargets = useMemo(() => {
    if (selectedTab !== Goals.TARGETS) return [];

    return (
      targets?.getGoalBankTargets?.edges.map((edge) => {
        const target = edge?.node;
        if (!target) return null;

        return (
          <GoalBankCard
            key={target.id}
            id={target.id ?? ""}
            title={target.targetName ?? ""}
            subtitle={target.goalBank?.goalName ?? ""}
            body={target.masteryCriteria ?? ""}
            onClick={() => {
              setSelectedGoal(target as TargetFormProps);
              setOpenEditForm(true);
            }}
            settings={{
              menuItems: [
                {
                  label: "Edit",
                  Icon: PencilIcon,
                  callback: () => {
                    setSelectedGoal(target as TargetFormProps);
                    setOpenEditForm(true);
                  },
                },
                {
                  label: "Move to Folder",
                  Icon: FolderIcon,
                  callback: () => {
                    setSelectedGoal(target as TargetFormProps);
                    setTargetMoveToFolderDialogOpen(true);
                  },
                },
                {
                  label: "Delete",
                  Icon: TrashIcon,
                  textColorClassName: "text-red-700",
                  callback: async () => {
                    const targetId = target.id;
                    try {
                      await deleteTarget({
                        input: {
                          folderId: selectedFolderId,
                          goalBankTargetId: targetId,
                        },
                      });
                      notifySuccess("Successfully Deleted Target");
                      await refetchTargets();
                    } catch (err) {
                      console.error("Failed To Delete Target", err);
                      notifyError("Failed To Delete Target");
                    }
                  },
                },
              ],
              openDirection: "to-bottom-left",
            }}
          />
        );
      }) || []
    );
  }, [selectedTab, targets, setSelectedGoal, setOpenEditForm]);
  const [deleteFolderId, setDeleteFolderId] = useState<string>("");
  const userFoldersSection = getUserFolderSection(userFolders);
  const orgFoldersSection = getOrgFolderSection(orgFolders, orgName);
  const [openDeleteFolderDialog, setDeleteFolderDialogOpen] = useState(false);

  const onSelectedFolder = (folder: Folder) => {
    setSelectedFolderId(folder.id);
  };

  const formTypeMapping = {
    [Goals.SHORT_TERM_GOALS]: {
      refetch: refetchStg,
      form: GoalBankShortTermGoalsAddNewForm,
      header: "No Goals Here Yet",
      subHeader: 'Click "Add New" to begin organizing your goals here.',
    },
    [Goals.TARGETS]: {
      refetch: refetchTargets,
      form: GoalBankTargetsAddNewForm,
      header: "No Targets Here Yet",
      subHeader: 'Click "Add New" to begin organizing your targets here.',
    },
  };

  const goalTypesToDisplay = [Goals.SHORT_TERM_GOALS, Goals.TARGETS];

  const goalsToDisplay = [displayShortTermGoals, displayTargets].flat();

  const showDeleteFolderDialog = (folderId: string) => {
    setDeleteFolderId(folderId);
    setDeleteFolderDialogOpen(true);
  };

  return (
    <>
      <FolderExplorer
        userType={userType}
        selectedFolderId={selectedFolderId}
        onDeleteFolder={(folderId: string) => {
          showDeleteFolderDialog(folderId);
        }}
        onEditFolder={(id: string, newValue: string) =>
          editFolder(findFolder(id, userFolders.concat(orgFolders)), newValue)
        }
        onCreateFolder={async (folderName: string, folderType: string) => {
          const newFolder = await createNewFolder(folderName, folderType);
          setSelectedFolderId(newFolder.id);
        }}
        onSelectedFolder={onSelectedFolder}
        onSearch={(value) => {
          setSearchTerm(value);
        }}
        showSearch={true}
        title="Saved Goals"
        folderSections={orgName ? [userFoldersSection, orgFoldersSection] : [userFoldersSection]}
        modalButton={<Button id="saved-goals-btn" appearance="secondary" text="Saved Goals" />}
        panel={
          <GoalBankPanel
            userType={userType}
            addNewButton={() => {
              const FormComponent = formTypeMapping[selectedTab].form;
              const refetchGoals = formTypeMapping[selectedTab].refetch;
              return (
                <FormComponent
                  folderId={selectedFolderId}
                  refetchGoals={() => {
                    if (refetchGoals) refetchGoals();
                  }}
                  formType={selectedTab}
                  hasGoals={goalsToDisplay.length > 0}
                />
              );
            }}
            isLoading={stgIsLoading || targetsIsLoading}
            key={userFolders.length + orgFolders.length}
            noFolders={!userFolders.length && !orgFolders.length}
            goalsToDisplay={goalsToDisplay}
            goalsTabs={getTabsToDisplay(selectedTab, setSelectedTab, goalTypesToDisplay)}
            onCreateFolder={createNewFolder}
            noGoalsEmptyState={{
              header: formTypeMapping[selectedTab].header,
              subHeader: formTypeMapping[selectedTab].subHeader,
            }}
          ></GoalBankPanel>
        }
      >
        {openEditForm && selectedGoal && selectedTab === Goals.SHORT_TERM_GOALS && (
          <GoalBankShortTermGoalsEditForm
            key={selectedFolderId}
            folderId={selectedFolderId}
            openEditForm={openEditForm}
            setOpenEditForm={setOpenEditForm}
            refetchGoals={refetchStg}
            selectedGoalBankShortTermGoal={selectedGoal as ShortTermGoalBankFormProps}
          />
        )}
        {openEditForm && selectedGoal && selectedTab === Goals.TARGETS && (
          <GoalBankTargetsEditForm
            key={selectedFolderId}
            folderId={selectedFolderId}
            openEditForm={openEditForm}
            setOpenEditForm={setOpenEditForm}
            refetchGoals={refetchTargets}
            selectedGoalBankTarget={selectedGoal as TargetFormProps}
          />
        )}
        <MoveShortTermGoalToFolder
          open={isSTGMoveToFolderDialogOpen}
          currentFolderId={selectedFolderId}
          goalBankShortTermId={selectedGoal?.id ?? undefined}
          foldersSelection={getFolderSelections(userFoldersSection, orgFoldersSection)}
          onCloseClick={() => {
            setSTGMoveToFolderDialogOpen(false);
          }}
          refetch={refetchStg}
        />
        <MoveTargetGoalToFolder
          open={isTargetMoveToFolderDialogOpen}
          currentFolderId={selectedFolderId}
          goalBankTargetId={selectedGoal?.id ?? undefined}
          foldersSelection={getFolderSelections(userFoldersSection, orgFoldersSection)}
          onCloseClick={() => setTargetMoveToFolderDialogOpen(false)}
          refetch={refetchTargets}
        />
        <DeleteFolderDialog
          open={openDeleteFolderDialog}
          folderName={findFolder(deleteFolderId, userFolders.concat(orgFolders)).name}
          onCancelClick={() => {
            setDeleteFolderId("");
            setDeleteFolderDialogOpen(false);
          }}
          onDeleteClick={() => {
            if (deleteFolderId == selectedFolderId) {
              setSelectedFolderId("");
            }
            deleteFolder(findFolder(deleteFolderId, userFolders.concat(orgFolders)));
            setDeleteFolderId("");
            setDeleteFolderDialogOpen(false);
          }}
        />
        <ConfirmationDialog
          open={openDeleteGoalDialog}
          onCancelClick={() => {
            setDeleteGoalId("");
            setDeleteGoalDialogOpen(false);
          }}
          onSubmitClick={() => {
            setDeleteGoalDialogOpen(false);
            if (deleteGoalId && selectedTab === Goals.SHORT_TERM_GOALS) {
              deleteSTGoal();
            }
          }}
          title="Delete Goal"
          content="Are you sure you want to delete this goal? This action cannot be undone."
          submitButtonText="Yes, Delete"
        />
      </FolderExplorer>
    </>
  );
};
